import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Camera, Upload, Loader2, Trash2, Send, CheckCircle2, Sparkles } from 'lucide-react';
import { SearchableSelect } from '../ui/SearchableSelect';
import { useToast } from '../ui/Toast';
import { ReceiptImage } from './ReceiptImage';
import { fileToResizedJpeg, fileToBase64 } from '../../utils/imageResize';
import { expenseCaptureApi, type ReceiptUpdateInput } from '../../services/expenseCaptureApi';
import type { ExpenseCategory, ExpenseCompany } from '../../types/expense';

interface Props {
    companies: ExpenseCompany[];
    categories: ExpenseCategory[];
    defaultCompanyCode: string | null;
    /** When set, resume an existing Draft straight into the edit step. */
    resumeReceiptId?: number;
    /** When set, the camera/upload already produced a file — skip the capture step and process it. */
    initialFile?: File;
    onDone: () => void;
    onCancel: () => void;
}

type Step = 'capture' | 'processing' | 'draft';

interface DraftForm {
    categoryId: number | null;
    expenseDate: string;
    vendor: string;
    grossAmount: string;
    vatAmount: string;
    netAmount: string;
    currency: string;
    description: string;
    declaration: Record<string, string>;
}

const EMPTY_FORM: DraftForm = {
    categoryId: null, expenseDate: '', vendor: '', grossAmount: '', vatAmount: '',
    netAmount: '', currency: 'EUR', description: '', declaration: {},
};

export function ExpenseCaptureWizard({ companies, categories, defaultCompanyCode, resumeReceiptId, initialFile, onDone, onCancel }: Props) {
    const { toast } = useToast();
    const fileRef = useRef<HTMLInputElement>(null);
    const cameraRef = useRef<HTMLInputElement>(null);

    const [step, setStep] = useState<Step>(resumeReceiptId || initialFile ? 'processing' : 'capture');
    const [companyCode, setCompanyCode] = useState<string>(defaultCompanyCode ?? 'dfr');
    const companyCodeRef = useRef(companyCode);
    companyCodeRef.current = companyCode;
    const [receiptId, setReceiptId] = useState<number | null>(null);
    const [form, setForm] = useState<DraftForm>(EMPTY_FORM);
    const [ocrConfidence, setOcrConfidence] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [processingMsg, setProcessingMsg] = useState('Uploading…');

    const selectedCategory = categories.find(c => c.id === form.categoryId) ?? null;

    // ---- Serialized autosave -------------------------------------------------
    const inflight = useRef<Promise<void> | null>(null);
    const dirty = useRef(false);
    const saveTimer = useRef<number | undefined>(undefined);
    const formRef = useRef(form);
    formRef.current = form;

    const buildBody = useCallback((): ReceiptUpdateInput => {
        const f = formRef.current;
        const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));
        return {
            companyCode: companyCodeRef.current,
            categoryId: f.categoryId,
            expenseDate: f.expenseDate || null,
            vendor: f.vendor || null,
            grossAmount: num(f.grossAmount),
            vatAmount: num(f.vatAmount),
            netAmount: num(f.netAmount),
            currency: f.currency || null,
            description: f.description || null,
            declaration: Object.keys(f.declaration).length ? f.declaration : null,
        };
    }, []);

    const runSave = useCallback(async () => {
        if (!receiptId || !dirty.current) return;
        if (inflight.current) { await inflight.current; }
        if (!dirty.current) return;
        dirty.current = false;
        inflight.current = expenseCaptureApi.updateReceipt(receiptId, buildBody())
            .then(() => undefined)
            .catch(() => { dirty.current = true; });
        await inflight.current;
        inflight.current = null;
        if (dirty.current) await runSave();
    }, [receiptId, buildBody]);

    const scheduleSave = useCallback(() => {
        dirty.current = true;
        window.clearTimeout(saveTimer.current);
        saveTimer.current = window.setTimeout(() => { void runSave(); }, 900);
    }, [runSave]);

    // Autosave when the form changes (only in draft step).
    useEffect(() => {
        if (step !== 'draft' || !receiptId) return;
        scheduleSave();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, step, receiptId]);

    const patch = (p: Partial<DraftForm>) => setForm(prev => ({ ...prev, ...p }));
    const patchDeclaration = (key: string, value: string) =>
        setForm(prev => ({ ...prev, declaration: { ...prev.declaration, [key]: value } }));

    // ---- Resume an existing Draft -------------------------------------------
    useEffect(() => {
        if (!resumeReceiptId) return;
        let active = true;
        setProcessingMsg('Loading draft…');
        expenseCaptureApi.getReceipt(resumeReceiptId)
            .then(({ receipt }) => {
                if (!active) return;
                let decl: Record<string, string> = {};
                try { decl = receipt.DeclarationJson ? JSON.parse(receipt.DeclarationJson) : {}; } catch { decl = {}; }
                setReceiptId(receipt.Id);
                setCompanyCode(receipt.CompanyCode);
                setOcrConfidence(receipt.OcrConfidence);
                setForm({
                    categoryId: receipt.CategoryId,
                    expenseDate: receipt.ExpenseDate ?? '',
                    vendor: receipt.Vendor ?? '',
                    grossAmount: receipt.GrossAmount != null ? String(receipt.GrossAmount) : '',
                    vatAmount: receipt.VatAmount != null ? String(receipt.VatAmount) : '',
                    netAmount: receipt.NetAmount != null ? String(receipt.NetAmount) : '',
                    currency: receipt.Currency || 'EUR',
                    description: receipt.Description ?? '',
                    declaration: decl,
                });
                dirty.current = false;
                setStep('draft');
            })
            .catch(() => { if (active) { toast('Could not load this draft.', 'error'); onCancel(); } });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resumeReceiptId]);

    // ---- Capture + OCR -------------------------------------------------------
    const handleFile = useCallback(async (file: File | undefined) => {
        if (!file) return;
        if (!companyCode) { toast('Please choose a country first.', 'error'); return; }
        setStep('processing');
        setProcessingMsg('Uploading…');
        try {
            let img: { base64: string; mime: string };
            try { img = await fileToResizedJpeg(file); }
            catch { img = await fileToBase64(file); } // HEIC on non-Safari → send original

            const created = await expenseCaptureApi.createReceipt({
                companyCode, fileBase64: img.base64, mimeType: img.mime, fileName: file.name,
            });
            setReceiptId(created.receiptId);
            if (created.duplicate) toast('You already captured this receipt — opening it.', 'info');

            // Seed currency from the company default (OCR may override below).
            const comp = companies.find(c => c.companyCode === companyCode);
            patch({ currency: created.currency || comp?.defaultCurrency || 'EUR' });

            setProcessingMsg('Reading the receipt…');
            try {
                const ocr = await expenseCaptureApi.ocrReceipt(created.receiptId);
                const e = ocr.extracted;
                setOcrConfidence(e.confidence);
                setForm(prev => ({
                    ...prev,
                    categoryId: ocr.categoryId ?? prev.categoryId,
                    expenseDate: e.expenseDate ?? prev.expenseDate,
                    vendor: e.vendor ?? prev.vendor,
                    grossAmount: e.grossAmount != null ? String(e.grossAmount) : prev.grossAmount,
                    vatAmount: e.vatAmount != null ? String(e.vatAmount) : prev.vatAmount,
                    netAmount: e.netAmount != null ? String(e.netAmount) : prev.netAmount,
                    currency: e.currency ?? prev.currency,
                }));
            } catch {
                toast('Could not auto-read the receipt — please fill the fields manually.', 'info');
            }
            dirty.current = false; // OCR already persisted server-side
            setStep('draft');
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Upload failed.', 'error');
            setStep('capture');
        }
    }, [companyCode, companies, toast]);

    // Process a file handed in from the Home camera/upload buttons (skip the capture step).
    const initialFileHandled = useRef(false);
    useEffect(() => {
        if (!initialFile || resumeReceiptId || initialFileHandled.current) return;
        initialFileHandled.current = true;
        void handleFile(initialFile);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFile]);

    // ---- Submit / discard ----------------------------------------------------
    const handleSubmit = useCallback(async () => {
        if (!receiptId) return;
        // Local completeness checks for a friendly message before hitting the server.
        if (!form.categoryId) { toast('Please choose a category.', 'error'); return; }
        if (form.grossAmount.trim() === '') { toast('Please enter the amount.', 'error'); return; }
        if (!form.expenseDate) { toast('Please enter the date.', 'error'); return; }
        for (const fld of selectedCategory?.fields ?? []) {
            if (fld.required && !(form.declaration[fld.key]?.trim())) {
                toast(`Please fill "${fld.label}".`, 'error'); return;
            }
        }
        setSubmitting(true);
        try {
            window.clearTimeout(saveTimer.current);
            dirty.current = true;
            await runSave();                       // flush pending edits
            await expenseCaptureApi.submitReceipt(receiptId);
            toast('Expense submitted.', 'success');
            onDone();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Could not submit.', 'error');
        } finally {
            setSubmitting(false);
        }
    }, [receiptId, form, selectedCategory, runSave, toast, onDone]);

    const handleDiscard = useCallback(async () => {
        if (!receiptId) { onCancel(); return; }
        try { await expenseCaptureApi.deleteReceipt(receiptId); } catch { /* ignore */ }
        onCancel();
    }, [receiptId, onCancel]);

    const companyOptions = companies.map(c => ({ id: c.companyCode, name: `${c.companyName} (${c.countryCode})` }));
    const categoryOptions = categories.map(c => ({ id: String(c.id), name: c.label }));

    // ---- Render --------------------------------------------------------------
    if (step === 'capture') {
        return (
            <div className="space-y-4">
                <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Capturing for</label>
                    <SearchableSelect
                        options={companyOptions}
                        value={companyCode}
                        onChange={setCompanyCode}
                        placeholder="Choose country / company"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                        Pick the country this expense belongs to (you can capture for a country other than your own).
                    </p>
                </div>

                <div
                    onClick={() => fileRef.current?.click()}
                    className="cursor-pointer rounded-2xl border-2 border-dashed border-gray-300 p-8 text-center hover:border-blue-300 hover:bg-gray-50"
                >
                    <Upload className="mx-auto mb-2 h-9 w-9 text-gray-400" />
                    <p className="text-sm font-medium text-gray-700">Tap to select a receipt photo</p>
                    <p className="mt-1 text-xs text-gray-500">JPEG / PNG / HEIC — auto-resized for upload</p>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden"
                        onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
                </div>

                <button
                    type="button"
                    onClick={() => cameraRef.current?.click()}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 py-3.5 font-medium text-white hover:bg-blue-700"
                >
                    <Camera className="h-5 w-5" /> Take a photo
                </button>
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />

                <button type="button" onClick={onCancel} className="w-full py-2 text-sm text-gray-500 hover:text-gray-700">
                    Cancel
                </button>
            </div>
        );
    }

    if (step === 'processing') {
        return (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-500">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <p className="text-sm font-medium">{processingMsg}</p>
            </div>
        );
    }

    // step === 'draft'
    return (
        <div className="space-y-4">
            {receiptId && (
                <ReceiptImage receiptId={receiptId} className="h-44 w-full rounded-xl border border-gray-200" />
            )}

            {ocrConfidence !== null && (
                <div className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700">
                    <Sparkles className="h-4 w-4" />
                    Auto-filled from the photo ({Math.round(ocrConfidence * 100)}% confidence) — please check the values.
                </div>
            )}

            <Field label="Country / company" required>
                <SearchableSelect
                    options={companyOptions}
                    value={companyCode}
                    onChange={v => { if (v) { setCompanyCode(v); scheduleSave(); } }}
                    placeholder="Choose country / company"
                />
                <p className="mt-1 text-xs text-gray-400">
                    Defaults to your own country — change it if this expense belongs to another.
                </p>
            </Field>

            <Field label="Category" required>
                <SearchableSelect
                    options={categoryOptions}
                    value={form.categoryId ? String(form.categoryId) : ''}
                    onChange={v => patch({ categoryId: v ? Number(v) : null })}
                    placeholder="Choose a category"
                />
            </Field>

            {/* Dynamic mandatory declaration fields for the chosen category */}
            {(selectedCategory?.fields ?? []).map(fld => (
                <Field key={fld.key} label={fld.label} required={fld.required}>
                    <input
                        type={fld.type === 'number' ? 'number' : fld.type === 'date' ? 'date' : 'text'}
                        value={form.declaration[fld.key] ?? ''}
                        onChange={e => patchDeclaration(fld.key, e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </Field>
            ))}

            <div className="grid grid-cols-2 gap-3">
                <Field label="Amount (gross)" required>
                    <input
                        type="number" inputMode="decimal" step="0.01"
                        value={form.grossAmount}
                        onChange={e => patch({ grossAmount: e.target.value })}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </Field>
                <Field label="Currency">
                    <input
                        type="text" maxLength={3}
                        value={form.currency}
                        onChange={e => patch({ currency: e.target.value.toUpperCase() })}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm uppercase focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Field label="Date" required>
                    <input
                        type="date"
                        value={form.expenseDate}
                        onChange={e => patch({ expenseDate: e.target.value })}
                        className="w-full min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </Field>
                <Field label="VAT amount">
                    <input
                        type="number" inputMode="decimal" step="0.01"
                        value={form.vatAmount}
                        onChange={e => patch({ vatAmount: e.target.value })}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </Field>
            </div>

            <Field label="Vendor / merchant">
                <input
                    type="text"
                    value={form.vendor}
                    onChange={e => patch({ vendor: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
            </Field>

            <Field label="Note (optional)">
                <textarea
                    rows={2}
                    value={form.description}
                    onChange={e => patch({ description: e.target.value })}
                    className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
            </Field>

            <div className="flex items-center gap-2 pt-1">
                <button
                    type="button"
                    onClick={handleDiscard}
                    className="flex items-center gap-1.5 rounded-xl border border-gray-300 px-3 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                    <Trash2 className="h-4 w-4" /> Discard
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 py-2.5 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Submit expense
                </button>
            </div>

            <p className="flex items-center justify-center gap-1 text-center text-xs text-gray-400">
                <CheckCircle2 className="h-3.5 w-3.5" /> Saved automatically as a draft
            </p>
        </div>
    );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
    // min-w-0 lets the field shrink inside a 2-col grid track — without it, inputs with an
    // intrinsic min-width (notably <input type="date">) overflow and overlap the neighbour.
    return (
        <div className="min-w-0">
            <label className="mb-1 block text-sm font-medium text-gray-700">
                {label}{required && <span className="ml-0.5 text-red-500">*</span>}
            </label>
            {children}
        </div>
    );
}
